/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Grid, Typography, Button } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { Icon } from "../../../components/Shared/Icon";
import { Spinner } from "../../../components/ui/spinner";

import { useGetDasInventoryGraphQuery, useGetDasInventoryQuery } from "../../../store/api/devices/deviceApiSlice";
import { IRemoteEquipment } from "../../../store/api/devices/types";
import { useStoreDispatch } from "../../../store/hooks";
import { setConfigCounts } from "../../../store/api/deviceConfig/reducers";
import { CONFIGUREROUTES } from "../../../globals/globals";
import DasDeviceDetailsDrawer from "../../../components/Drawers/DasDeviceDetailsDrawer";
import { selectedDeviceSelector } from "../../../store/api/devices/selectors";
import { DasTopologyView } from "../../../components/DasTopology";
import { detectPlatform } from "../../../components/DasTopology/utils/detectPlatform";
import { searchKeywordSelector } from "../../../store/search/selector";
import { resetKeyword } from "../../../store/search/reducer";
import { LOCAL_STORAGE } from "../../../globals/globals";
import { setOpenToast } from "../../../store/helpers/helperReducers";
import {
  dasInventoryEquipmentTypeSelector,
  dasInventoryStatusSelector,
  dasInventoryBandSelector,
} from "../../../store/filters/dasInventory/selectors";
import { resetDasInventoryFilters } from "../../../store/filters/dasInventory/reducer";

const DasTopology: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const locationPath = location?.pathname.split("/");
  const deviceKey = locationPath?.[2];
  const dispatch = useStoreDispatch();
  const selectedDeviceDetail = useSelector(selectedDeviceSelector);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<IRemoteEquipment | null>(null);

  // Get filter values from Redux
  const selectedEquipmentType = useSelector(dasInventoryEquipmentTypeSelector);
  const selectedStatus = useSelector(dasInventoryStatusSelector);
  const selectedBand = useSelector(dasInventoryBandSelector);
  const searchKeyword = useSelector(searchKeywordSelector);

  const { data: listData = [], isLoading: listLoading, refetch: refetchList } = useGetDasInventoryQuery(
    { deviceKey }, 
    { 
      skip: !deviceKey,
      pollingInterval: 30000,
      refetchOnMountOrArgChange: 30, // Use cache if data is less than 30 seconds old
    }
  );
  
  const { data: graphData, isLoading: graphLoading, refetch: refetchGraph } = useGetDasInventoryGraphQuery(
    { deviceKey }, 
    { 
      skip: !deviceKey,
      pollingInterval: 30000,
      refetchOnMountOrArgChange: 30, // Use cache if data is less than 30 seconds old
    }
  );

  // Auto-detect platform from equipment data
  const detectedPlatform = useMemo(() => {
    const platform = detectPlatform(listData);
    return platform;
  }, [listData]);

  useEffect(() => {
    // Update config counts for this page
    dispatch(setConfigCounts({ [CONFIGUREROUTES.DASTOPOLOGY.Path]: listData.length }));
  }, [dispatch, listData.length]);

  // Reset filters when component unmounts (navigating away)
  useEffect(() => {
    return () => {
      dispatch(resetDasInventoryFilters());
      dispatch(resetKeyword());
    };
  }, [dispatch]);

  // Update selectedDevice with fresh data when drawer is open and data refreshes
  useEffect(() => {
    if (drawerOpen && selectedDevice && listData.length > 0) {
      const updatedDevice = listData.find((device) => device.objectKey === selectedDevice.objectKey);
      if (updatedDevice) {
        setSelectedDevice(updatedDevice);
      }
    }
  }, [listData, drawerOpen, selectedDevice?.objectKey]);

  const handleRowClick = (device: IRemoteEquipment) => {
    setSelectedDevice(device);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
  };


  return (
    <Grid container>
      <Grid item xs={12}>
        {graphLoading && <Typography sx={{ p: 2 }}>Loading topology...</Typography>}
        {!graphLoading && !graphData && <Typography sx={{ p: 2 }}>No topology data available.</Typography>}
        {!graphLoading && graphData && (
          <DasTopologyView
            nodes={graphData.nodes}
            edges={graphData.edges}
            rootId={graphData.rootId}
            onDeviceClick={handleRowClick}
            listData={listData}
            platform={detectedPlatform}
            filterEquipmentType={selectedEquipmentType}
            filterStatus={selectedStatus}
            filterBand={selectedBand}
            filterSearchKeyword={searchKeyword}
          />
        )}
      </Grid>
      <DasDeviceDetailsDrawer
        open={drawerOpen}
        device={selectedDevice}
        onClose={handleCloseDrawer}
      />

    </Grid>
  );
};

export default DasTopology;
